import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Bot, ArrowRight } from "lucide-react";
import { gtmChatOpen, gtmChatMessage } from "@/lib/gtm";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("nfw_chat_session");
  if (!id) {
    id = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    localStorage.setItem("nfw_chat_session", id);
  }
  return id;
};

const QUICK_REPLIES = [
  "What's included in the $499 package?",
  "How fast can you build my website?",
  "Do you provide hosting?",
  "Can I see some examples?",
];

export default function ChatBot() {
  const [open, setOpen] = useState(false);
  const [registered, setRegistered] = useState(() => !!localStorage.getItem("nfw_chat_user"));
  const [userName, setUserName] = useState(() => {
    try { return JSON.parse(localStorage.getItem("nfw_chat_user"))?.name || ""; } catch { return ""; }
  });
  const [regName, setRegName] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regLoading, setRegLoading] = useState(false);

  const [messages, setMessages] = useState([
    { role: "assistant", content: "G'day! I'm Sarah from No Fuss Websites. How can I help you today? We build professional websites for just $499 — live within 48 hours, guaranteed!" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(getSessionId);
  const [msgCount, setMsgCount] = useState(0);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (open && bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth" });
  }, [messages, open]);

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!regName.trim() || !regEmail.trim()) return;
    setRegLoading(true);
    try {
      await axios.post(`${API}/chat/register`, { session_id: sessionId, name: regName.trim(), email: regEmail.trim() });
      const userData = { name: regName.trim(), email: regEmail.trim() };
      localStorage.setItem("nfw_chat_user", JSON.stringify(userData));
      setUserName(regName.trim());
      setRegistered(true);
      setMessages(prev => [...prev, { role: "assistant", content: `Thanks ${regName.trim()}! How can I help you today?` }]);
    } catch {
      setRegistered(true); // allow chat even if registration fails
    } finally { setRegLoading(false); }
  };

  const sendMessage = async (text) => {
    const msg = text || input.trim();
    if (!msg || loading) return;
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: msg }]);
    setLoading(true);
    const newCount = msgCount + 1;
    setMsgCount(newCount);
    gtmChatMessage(newCount);
    try {
      const res = await axios.post(`${API}/chat`, { session_id: sessionId, message: msg });
      setMessages(prev => [...prev, { role: "assistant", content: res.data.response }]);
    } catch {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, having a bit of tech trouble. Give us a call on 0466 476 264 and we'll sort you out!" }]);
    } finally { setLoading(false); }
  };

  const handleKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  return (
    <div className="fixed bottom-20 right-6 z-50 flex flex-col items-end gap-3" data-testid="chatbot-container">
      {open && (
        <div
          className="fixed inset-0 md:relative md:inset-auto md:w-96 bg-white md:border-2 md:border-black md:rounded-2xl overflow-hidden flex flex-col z-50 md:h-[500px]"
          style={{ boxShadow: "" }}
          data-testid="chatbot-window"
        >
          <style>{`@media(min-width:768px){[data-testid="chatbot-window"]{box-shadow:6px 6px 0px 0px rgba(0,0,0,1)!important}}`}</style>
          {/* Header */}
          <div className="bg-[#FFD600] border-b-2 border-black p-4 flex items-center justify-between shrink-0">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-black rounded-full flex items-center justify-center">
                <Bot size={20} className="text-[#FFD600]" />
              </div>
              <div>
                <p className="font-black text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>Sarah</p>
                <div className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-green-600 rounded-full" />
                  <p className="text-xs font-semibold">Online now</p>
                </div>
              </div>
            </div>
            <button onClick={() => setOpen(false)}
              className="w-8 h-8 flex items-center justify-center border-2 border-black rounded-lg hover:bg-black hover:text-[#FFD600] transition-colors"
              data-testid="chatbot-close">
              <X size={14} />
            </button>
          </div>

          {!registered ? (
            /* Registration Form */
            <form onSubmit={handleRegister} className="flex-1 flex flex-col justify-center p-6 space-y-4" data-testid="chatbot-register-form">
              <div className="text-center mb-2">
                <p className="font-black text-lg" style={{ fontFamily: "Outfit, sans-serif" }}>Before we chat...</p>
                <p className="text-sm text-gray-600">Just your name and email so we can follow up if needed.</p>
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider mb-1 block">Your Name</label>
                <input value={regName} onChange={e => setRegName(e.target.value)} placeholder="e.g. John Smith"
                  className="w-full border-2 border-black rounded-xl px-3 py-2.5 text-sm" data-testid="chatbot-reg-name" required />
              </div>
              <div>
                <label className="text-xs font-black uppercase tracking-wider mb-1 block">Email Address</label>
                <input type="email" value={regEmail} onChange={e => setRegEmail(e.target.value)} placeholder="john@example.com"
                  className="w-full border-2 border-black rounded-xl px-3 py-2.5 text-sm" data-testid="chatbot-reg-email" required />
              </div>
              <button type="submit" disabled={regLoading || !regName.trim() || !regEmail.trim()}
                className="w-full bg-[#FFD600] border-2 border-black rounded-xl py-3 font-black text-sm flex items-center justify-center gap-2 hover:-translate-y-0.5 transition-transform disabled:opacity-50"
                data-testid="chatbot-reg-submit">
                {regLoading ? "Starting..." : "Start Chat"} <ArrowRight size={14} />
              </button>
            </form>
          ) : (
            <>
              {/* Messages */}
              <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#FDFDFD]">
                {messages.map((m, i) => (
                  <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[80%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                      m.role === "user" ? "bg-black text-white rounded-br-sm" : "bg-white border-2 border-black rounded-bl-sm"
                    }`} style={m.role === "assistant" ? { boxShadow: "2px 2px 0px 0px rgba(0,0,0,1)" } : {}}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {loading && (
                  <div className="flex justify-start">
                    <div className="bg-white border-2 border-black px-4 py-3 rounded-2xl rounded-bl-sm" style={{ boxShadow: "2px 2px 0px 0px rgba(0,0,0,1)" }}>
                      <div className="flex gap-1 items-center">
                        {[0, 1, 2].map(i => (
                          <div key={i} className="w-2 h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: `${i * 0.15}s` }} />
                        ))}
                      </div>
                    </div>
                  </div>
                )}
                <div ref={bottomRef} />
              </div>

              {/* Quick replies */}
              {messages.length <= 2 && (
                <div className="px-4 pb-2 flex flex-wrap gap-2 shrink-0">
                  {QUICK_REPLIES.map((q, i) => (
                    <button key={i} onClick={() => sendMessage(q)}
                      className="text-xs font-semibold px-3 py-1.5 bg-[#F4F4F5] border-2 border-black rounded-full hover:bg-[#FFD600] transition-colors"
                      data-testid={`quick-reply-${i}`}>
                      {q}
                    </button>
                  ))}
                </div>
              )}

              {/* Input */}
              <div className="p-4 border-t-2 border-black bg-white flex gap-2 shrink-0">
                <input type="text" value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKey}
                  placeholder="Ask me anything..."
                  className="flex-1 border-2 border-black rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-[#0055FF]"
                  data-testid="chatbot-input" />
                <button onClick={() => sendMessage()} disabled={!input.trim() || loading}
                  className="w-10 h-10 bg-[#FFD600] border-2 border-black rounded-xl flex items-center justify-center hover:-translate-y-0.5 transition-transform disabled:opacity-40"
                  data-testid="chatbot-send">
                  <Send size={16} />
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Toggle Button - hidden on mobile when chat is open */}
      <button
        onClick={() => { if (!open) gtmChatOpen(); setOpen(!open); }}
        className={`w-14 h-14 rounded-full border-2 border-black flex items-center justify-center transition-all duration-200 hover:-translate-y-1 ${
          open ? "hidden md:flex bg-black text-white" : "bg-[#FFD600] text-black animate-pulse-ring"
        }`}
        style={{ boxShadow: "4px 4px 0px 0px rgba(0,0,0,1)" }}
        data-testid="chatbot-toggle"
        aria-label="Chat with us"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
      </button>
    </div>
  );
}
